import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import { useSelector } from "react-redux";
import axios from "axios";
import Typography from "@mui/material/Typography";
import moment from "moment";
import MUIDataTable from "mui-datatables";
import { Button, Grid } from "@mui/material";
import BasicDateRangePicker from "../../../../components/common/datepicker";
import { BASE_URL } from "../../../../components/config/config";

// Permission check function
const hasPermission = (permissions, path) => {
  if (!permissions) return false;

  if (permissions.full_access) return true;

  const pathParts = path.split(".");
  let current = permissions;

  for (const part of pathParts) {
    if (current[part] === undefined) return false;
    current = current[part];
    if (typeof current === "boolean") return current;
  }

  return false;
};

const canViewConsumerTransaction = (permissions) =>
  hasPermission(
    permissions,
    "transaction_wallet_details.update_transaction_wallet_details.view_consumer_transaction"
  ) || hasPermission(permissions, "transaction_wallet_details.full_access");

const columns = [
  {
    name: "createdAt",
    label: "Transaction Date",
    options: {
      filter: false,
      sort: true,
      customBodyRender: (value) => moment(value).format("Do MMM, hh:ss A"),
    },
  },
  {
    name: "userId",
    label: "User ID",
    options: {
      filter: true,
      sort: false,
    },
  },
  {
    name: "consumer_name",
    label: "Customer Name",
    options: {
      filter: true,
      sort: false,
    },
  },
  {
    name: "totalRechargeAmount",
    label: "Total Recharge Amount",
    options: {
      filter: false,
      sort: false,
    },
  },
  {
    name: "totalTransactions",
    label: "Total Transactions",
    options: {
      filter: false,
      sort: false,
    },
  },
  {
    name: "wallet_remain",
    label: "Balance",
  },
];

const consumerTransactions = [
  {
    name: "createdAt",
    label: "Transaction Date",
    options: {
      filter: false,
      sort: true,
      customBodyRender: (value) => moment(value).format("Do MMM, hh:ss A"),
    },
  },
  {
    name: "userId",
    label: "User ID",
    options: {
      filter: false,
      sort: false,
    },
  },
  {
    name: "transactionId",
    label: "Transaction ID",
    options: {
      filter: false,
      sort: false,
    },
  },
  {
    name: "amount",
    label: "Recharge Amount",
    options: {
      filter: false,
      sort: false,
    },
  },
  {
    name: "status",
    label: "Status",
    options: {
      filter: false,
      sort: false,
    },
  },
];

const options = {
  selectableRows: "none",
  responsive: "standard",
  filterType: "checkbox",
  filter: true,
  textLabels: {
    filter: "Filter",
    reset: "Reset",
    close: "Close",
  },
  customFilterDialogFooter: (currentFilterList, applyNewFilters) => (
    <div style={{ padding: "20px" }}>
      <Button variant="contained" onClick={() => applyNewFilters()}>
        Apply
      </Button>
    </div>
  ),
  customFilterDialogFooterStyle: {
    borderTop: "1px solid #ccc",
  },
  customFilterDialogFooterPosition: "right",
  customFilterDialogWidth: "500px",
};

const stylesCss = {
  inputBox: {
    width: "100%",
    marginBottom: "10px",
  },
  gridStyle: {
    width: "80%",
    margin: "auto",
  },
  paddingInnerGrids: {
    paddingRight: "10px",
    paddingLeft: "10px",
    paddingTop: "2px",
  },
};

export default function ConsumerWalletTable() {
  const globalState = useSelector((state) => state.AdminReducer);
  const userPermissions = globalState?.adminDetails?.role_id?.permissions;
  const [loader, setLoader] = React.useState(false);
  const [selectedUserId, setSelectedUserId] = React.useState(null);
  const [userTransactions, setUserTransactions] = React.useState([]);
  const [accessToken, setAccessToken] = React.useState("");
  const [rows, setRows] = useState([]);

  // Move useEffect to the top level, before any conditional returns
  useEffect(() => {
    setAccessToken(globalState?.userTokens?.accessToken);
    fetchData();
  }, [globalState]);

  const refreshData = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}admin/wallet-transactions-consumer`,
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );
      setRows(response.data?.data);
    } catch (error) {
      console.log("Error fetching data:", error);
      return [];
    }
  };

  const onClickFilter = async (value) => {
    let payload = {
      start_date: value[0],
      end_date: value[1],
    };

    try {
      const response = await axios.get(
        `${BASE_URL}admin/wallet-transactions-consumer`,
        {
          headers: {
            Authorization: accessToken,
          },
          params: payload,
        }
      );
      setRows(response.data?.data);
    } catch (error) {
      console.log("Error fetching data:", error);
      return [];
    }
  };

  const fetchData = async () => {
    try {
      setLoader(true);
      const response = await axios.get(
        `${BASE_URL}admin/wallet-transactions-consumer`,
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );

      const sortedData = response.data?.data.sort((a, b) => {
        return (
          new Date(b?.createdAt).toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            second: "numeric",
            hour12: true,
          }),
          -new Date(a?.createdAt).toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            second: "numeric",
            hour12: true,
          })
        );
      });

      const groupedRows = response.data?.data.reduce((acc, transaction) => {
        const { userId } = transaction;
        if (!acc[userId]) {
          acc[userId] = [];
        }
        acc[userId].push(transaction);
        return acc;
      }, {});

      Object.values(groupedRows).forEach((transactions) => {
        transactions.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
      });

      const finalRows = Object.values(groupedRows).map((transactions) => {
        const totalRechargeAmount = transactions.reduce(
          (total, transaction) => total + transaction.amount,
          0
        );
        return {
          ...transactions[0],
          totalRechargeAmount,
          totalTransactions: transactions.length,
        };
      });

      finalRows.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setRows(finalRows);
    } catch (error) {
      console.log("Error fetching data:", error);
      setRows([]);
    } finally {
      setLoader(false);
    }
  };

  const handleRowClick = (userId) => {
    setSelectedUserId(userId);
    fetchUserTransactions(userId);
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: "smooth",
    });
  };

  const fetchUserTransactions = async (userId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}admin/wallet-transactions-consumer-byId`,
        {
          headers: {
            Authorization: accessToken,
          },
          params: {
            userId: userId,
          },
        }
      );

      const sortedTransactions = response.data?.data.sort((a, b) => {
        return (
          new Date(b?.createdAt).toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            second: "numeric",
            hour12: true,
          }),
          -new Date(a?.createdAt).toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            second: "numeric",
            hour12: true,
          })
        );
      });

      setUserTransactions(sortedTransactions);
    } catch (error) {
      console.log("Error fetching user transactions:", error);
      setUserTransactions([]);
    }
  };

  const handleCloseDetailView = () => {
    setSelectedUserId(null);
    setUserTransactions([]);
  };

  // Check permissions - this should be after all hooks
  if (!canViewConsumerTransaction(userPermissions)) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          No permission to view consumer transactions.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box style={stylesCss.paddingInnerGrids}>
        <BasicDateRangePicker
          onClickFilter={onClickFilter}
          refreshData={refreshData}
        />
      </Box>
      <MUIDataTable
        data={rows}
        columns={columns}
        options={{ ...options, onRowClick: handleRowClick }}
      />
      {selectedUserId && (
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "right",
            }}
          >
            <Typography variant="h6">
              {userTransactions.length > 0
                ? userTransactions[0].consumer_name
                : ""}
            </Typography>
            <Button variant="contained" onClick={handleCloseDetailView}>
              Close
            </Button>
          </div>
          <MUIDataTable
            data={userTransactions}
            columns={consumerTransactions}
            options={options}
          />
        </div>
      )}
    </>
  );
}
